import 'react-tooltip/dist/react-tooltip.css';

import React, { useState, useMemo  } from 'react';
import { Tooltip } from 'react-tooltip';
import {
  Clock,
  Compass,
  Gift,
  Info,
  Repeat as RepeatIcon,
  TrendingUp,
  UserPlus,
  ChevronRight,
  Users,
  BookOpen,
  MapPin,
  Briefcase,
  Heart,
} from 'react-feather';

import styles from './Page1_Goals.module.scss';

const TOTAL_STEPS = 5;
const CURRENT_STEP = 1;

const GOALS = [
  {
    key: 'attract_new_customers',
    title: 'Attract New Customers',
    description: 'Attract first-time visitors and expand your reach.',
    tooltip: 'Create a coupon designed to expand your reach and grow your customer base by attracting first-time visitors to your business.',
    icon: UserPlus,
  },
  {
    key: 'increase_specific_sales',
    title: 'Increase Sales of a Specific Product/Service',
    description: 'Boost demand for a particular item or service.',
    tooltip: 'Create a coupon designed to boost demand for a particular item or service, manage inventory, or highlight a high-margin offering.',
    icon: TrendingUp,
  },
  {
    key: 'drive_repeat_business',
    title: 'Drive Repeat Business',
    description: 'Encourage existing customers to return more often.',
    tooltip: 'Create a coupon designed to foster customer loyalty and encourage existing patrons to return more frequently, building long-term relationships.',
    icon: RepeatIcon,
  },
  {
    key: 'fill_slow_periods',
    title: 'Fill Slow Periods / Boost Off-Peak Sales',
    description: 'Attract customers during quieter hours or days.',
    tooltip: 'Create a coupon designed to generate traffic and sales during traditionally quiet periods, maximizing your operational efficiency and revenue.',
    icon: Clock,
  },
  {
    key: 'special_promotion',
    title: 'Special Promotion',
    description: 'Capitalize on holidays, seasons, or special events with timely offers aligned to consumer trends.',
    tooltip: 'Create a coupon designed to capitalize on holidays, seasons, or special events to create timely and relevant offers that align with consumer trends.',
    icon: Gift,
  },
];

const ALL_ITEMS = [
  ...GOALS,
  {
    key: 'no_goal',
    title: 'Start with No Goal In Mind',
    description: 'Manually build your own coupon from scratch.',
    tooltip: 'Manually build your own coupon from scratch without a predefined goal.',
    icon: Compass,
    isScratch: true,
  },
];

const AUDIENCES = [
  { key: 'all',      label: 'All Customers / Not Restricted', icon: null },
  { key: 'students', label: 'Students', icon: BookOpen },
  { key: 'tourist',  label: 'Tourists', icon: MapPin },
  { key: 'adults',   label: 'Young Adults', icon: Briefcase },
  { key: 'families', label: 'Families', icon: Users },
  { key: 'seniors',  label: 'Seniors', icon: Heart },
];

const RED = '#c0392b';

/* ── Responsive helpers ────────────────────────────────────────────────────── */
const useWindowWidth = () => {
  const [width, setWidth] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  React.useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return width;
};

const getColumns = (width) => {
  if (width < 641) return 1;
  if (width < 821) return 2;
  return 3;
};

/* ── GoalCard ───────────────────────────────────────────────────────────────── */
function GoalCard({ item, isActive, onClick }) {
  const [hov, setHov] = useState(false);
  const GoalIcon = item.icon;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isActive}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: 'relative',
        background: isActive ? RED : hov ? '#fff' : 'rgba(255,255,255,0.75)',
        border: '2px solid transparent',
        borderRadius: 16,
        padding: '20px 16px 18px',
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        boxShadow: isActive
          ? '0 4px 20px rgba(192,57,43,0.28)'
          : '0 2px 8px rgba(0,0,0,0.05)',
        transition: 'background .15s, box-shadow .15s',
        minHeight: 160,
        width: '100%',
      }}
    >
      {/* Info button */}
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        data-tooltip-id={`tip-${item.key}`}
        data-tooltip-content={item.tooltip}
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          background: 'none',
          border: isActive
            ? '1.5px solid rgba(255,255,255,0.7)'
            : `1.5px solid ${RED}`,
          borderRadius: '50%',
          width: 26,
          height: 26,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: isActive ? '#fff' : RED,
          padding: 0,
        }}
      >
        <Info size={13} />
      </button>
      <Tooltip
        id={`tip-${item.key}`}
        place="top"
        style={{ fontSize: 12, maxWidth: 220 }}
      />

      {/* Icon box */}
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: isActive
            ? 'rgba(255,255,255,0.20)'
            : 'rgba(192,57,43,0.10)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <GoalIcon
          size={22}
          color={isActive ? '#fff' : RED}
          strokeWidth={1.8}
        />
      </div>

      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: isActive ? '#fff' : '#1a1a1a',
          lineHeight: 1.35,
          paddingRight: 8,
        }}
      >
        {item.title}
      </div>
      <div
        style={{
          fontSize: 12,
          color: isActive ? 'rgba(255,255,255,0.75)' : '#888',
          lineHeight: 1.5,
        }}
      >
        {item.description}
      </div>
    </button>
  );
}

/* ── Page1_Goals ────────────────────────────────────────────────────────────── */
export default function Page1_Goals({ selectedGoal, onNext, onClose }) {
  const [activeKey, setActiveKey] = useState(selectedGoal || null);
  const [audience, setAudience] = useState(null);
  const width = useWindowWidth();
  const columns = getColumns(width);

  const handleCardClick = (item) => {
    if (item.isScratch) {
      onNext?.({
        goalKey: undefined,
        startFromScratch: true,
        goDirectToEdit: true,
        resetWizardDraft: true,
        clearImages: true,
      });
      return;
    }
    if (activeKey === item.key) {
      setActiveKey(null);
      setAudience(null);
    } else {
      setActiveKey(item.key);
      setAudience(null);
    }
  };

  const handleNext = () => {
    if (!activeKey) return;
    onNext?.({
      goalKey: activeKey,
      audience: audience ? [audience] : [],
      startFromScratch: false,
      goDirectToEdit: false,
    });
  };

  /* Build rows based on current column count */
  const visibleItems = activeKey
    ? ALL_ITEMS.filter((g) => g.key === activeKey)
    : ALL_ITEMS;

  const rows = [];
  for (let i = 0; i < ALL_ITEMS.length; i += columns) {
    rows.push(ALL_ITEMS.slice(i, i + columns));
  }

  const activeIndex = ALL_ITEMS.findIndex((g) => g.key === activeKey);
  const activeRow =
    activeIndex === -1 ? -1 : Math.floor(activeIndex / columns);

  /* ── Container padding responsive ── */
  const containerPadding =
    width < 480
      ? '20px 14px 32px'
      : width < 641
      ? '24px 18px 36px'
      : '32px 40px 44px';

  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          'linear-gradient(150deg, #f7e8e8 0%, #eddcdc 50%, #e6cdcd 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Inter','Segoe UI',sans-serif",
        padding: width < 641 ? '16px 12px 32px' : 24,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 900,
          position: 'relative',
          borderRadius: 22,
          padding: containerPadding,
        }}
      >
        {/* ── Progress bar ── */}
        <div style={{ marginBottom: 28, paddingRight: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
              const n = i + 1;
              const isActive = n === CURRENT_STEP;
              const isDone = n < CURRENT_STEP;
              return (
                <React.Fragment key={n}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      flexShrink: 0,
                      background:
                        isActive || isDone ? RED : 'transparent',
                      border:
                        isActive || isDone ? 'none' : '2px solid #ccc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      color: isActive || isDone ? '#fff' : '#bbb',
                    }}
                  >
                    {n}
                  </div>
                  {i < TOTAL_STEPS - 1 && (
                    <div
                      style={{
                        flex: 1,
                        height: 2,
                        background: isDone ? RED : '#ddd',
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* ── Header ── */}
        <h2
          style={{
            margin: '0 0 8px',
            fontSize: width < 480 ? 20 : width < 641 ? 22 : 26,
            fontWeight: 800,
            color: '#1a1a1a',
            letterSpacing: '-0.3px',
          }}
        >
          Choose a goal for your coupon
        </h2>
        <p
          style={{
            margin: '0 0 24px',
            fontSize: 14,
            color: '#777',
            lineHeight: 1.65,
            maxWidth: 500,
          }}
        >
          Let's craft a coupon that truly works for your business. Select the
          primary goal you want to achieve, and we'll help you build the
          perfect deal.
        </p>

        {/* ── Goal grid rows with audience panel injected after active row ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {rows.map((rowItems, rowIndex) => {
            const rowContainsActive = rowIndex === activeRow;
            /* When a card is active, hide all rows except the one with the active card */
            if (activeKey && !rowContainsActive) return null;

            return (
              <React.Fragment key={rowIndex}>
                {/* Card row */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columns}, 1fr)`,
                    gap: 14,
                  }}
                >
                  {rowItems.map((item) =>
                    activeKey && item.key !== activeKey ? (
                      /* Invisible placeholder keeps grid column position */
                      <div key={item.key} />
                    ) : (
                      <GoalCard
                        key={item.key}
                        item={item}
                        isActive={activeKey === item.key}
                        onClick={() => handleCardClick(item)}
                      />
                    )
                  )}
                </div>

                {/* Audience panel — injected after the row containing the active card */}
                {activeKey && rowContainsActive && (
                  <div
                    style={{
                      background: '#fff',
                      borderRadius: 16,
                      padding:
                        width < 480
                          ? '18px 14px 14px'
                          : '24px 28px 20px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      marginTop: 4,
                    }}
                  >
                    <p
                      style={{
                        margin: '0 0 4px',
                        fontSize: 16,
                        fontWeight: 700,
                        color: '#1a1a1a',
                      }}
                    >
                      Who are you looking for?
                    </p>
                    <p
                      style={{
                        margin: '0 0 18px',
                        fontSize: 13,
                        color: '#aaa',
                      }}
                    >
                      Select the audience you want to target with this coupon
                    </p>

                    {/* Audience chips */}
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: width < 480 ? 8 : 10,
                        marginBottom: 18,
                      }}
                    >
                      {AUDIENCES.map((a) => {
                        const sel = audience === a.label;
                        const AIcon = a.icon;
                        return (
                          <button
                            key={a.key}
                            type="button"
                            onClick={() =>
                              setAudience(sel ? null : a.label)
                            }
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 7,
                              padding:
                                width < 480
                                  ? '8px 14px'
                                  : '10px 20px',
                              borderRadius: 999,
                              border: sel
                                ? 'none'
                                : '1.5px solid #ddd',
                              background: sel ? '#1a1a1a' : '#fff',
                              color: sel ? '#fff' : '#333',
                              fontSize: width < 480 ? 12 : 14,
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'background .15s',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {AIcon && (
                              <AIcon
                                size={15}
                                color={sel ? '#fff' : RED}
                              />
                            )}
                            {a.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* Next button */}
                    <div
                      style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                      <button
                        type="button"
                        onClick={handleNext}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          background: RED,
                          color: '#fff',
                          border: 'none',
                          borderRadius: 10,
                          padding:
                            width < 480
                              ? '10px 20px'
                              : '12px 28px',
                          fontSize: width < 480 ? 14 : 15,
                          fontWeight: 700,
                          cursor: 'pointer',
                          boxShadow:
                            '0 4px 14px rgba(192,57,43,0.30)',
                        }}
                      >
                        Next <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}